import { computed, InjectionKey, ref, watch } from "@vue/composition-api";
import { useRequest } from "@/helpers/hooks/useRequest";
import { getAnnouncements } from "@/api/index";
import { ApiResponse } from "@/api/types";
import { useComponentInstance } from "./useComponentInstance";
import { getPartnerUserAndContracts } from "../auth-helper";
import {
  TERMS_OF_USE_FOR_JOB_NAME,
  TERMS_OF_USE_FOR_PLAN_NAME,
  TERMS_OF_USE_FOR_SCOUT_NAME,
  TERMS_OF_USE_FOR_AGENT_SCOUT_NAME,
  EXTERNAL_DATA_PROCESSORS
} from "@/constants/terms";

type TermsPopupStore = ReturnType<typeof useTermsPopup>;

export const TERMS_POPUP: InjectionKey<TermsPopupStore> = Symbol("TERMS_POPUP");

export const useTermsPopup = () => {
  const root = useComponentInstance();
  const partnerUser = computed(
    () => root.$store.state.domains.user.partnerUser
  );

  const isLoggedIn = computed<ApiResponse.PartnerUser | undefined>(
    () => root.$store.getters["domains/user/isLoggedIn"]
  );

  const isTermsPage = computed<boolean>(() => {
    return (
      !!root.$route.name &&
      [
        TERMS_OF_USE_FOR_JOB_NAME,
        TERMS_OF_USE_FOR_PLAN_NAME,
        TERMS_OF_USE_FOR_SCOUT_NAME,
        TERMS_OF_USE_FOR_AGENT_SCOUT_NAME,
        EXTERNAL_DATA_PROCESSORS
      ].includes(root.$route.name)
    );
  });

  const isTermsPopupVisible = ref<boolean>(false);

  const [
    { data: termsPopupAnnouncementList },
    runGetTermsPopupAnnouncements
  ] = useRequest(() =>
    getAnnouncements({
      announce_type: "terms",
      announce_method: "popup"
    })
  );

  const unagreedTermsPopupAnnouncementList = ref<ApiResponse.Announcements>();
  const displayedAnnouncement = ref<ApiResponse.Announcement>();

  const setDisplayedAnnouncement = () => {
    displayedAnnouncement.value = unagreedTermsPopupAnnouncementList.value
      ? unagreedTermsPopupAnnouncementList.value[0]
      : undefined;
  };

  const onCompleteTermsAgree = async () => {
    await getPartnerUserAndContracts();
    root.$store.commit("common/resetApiError");
    root.$router.push({ name: "Home" }, () => {});
  };

  const showNextAnnouncement = async (delay = 500) => {
    setDisplayedAnnouncement();
    setTimeout(() => {
      isTermsPopupVisible.value = true;
    }, delay);
  };

  const deleteAnnouncementFromUnagreedList = (serviceTermId: number) => {
    unagreedTermsPopupAnnouncementList.value = unagreedTermsPopupAnnouncementList.value?.filter(
      announcement => {
        return announcement.service_term.id !== serviceTermId;
      }
    );
  };

  const isAgreedTerms = (termsId: number): boolean => {
    return (
      partnerUser.value &&
      partnerUser.value.partner.agreed_service_terms_ids.includes(termsId)
    );
  };

  const allTermsPopupAgreed = computed(() => {
    return (
      !unagreedTermsPopupAnnouncementList.value ||
      unagreedTermsPopupAnnouncementList.value.length === 0
    );
  });

  watch(
    isLoggedIn,
    async currentIsLoggedIn => {
      if (!currentIsLoggedIn) return;
      await getPartnerUserAndContracts();
      await runGetTermsPopupAnnouncements();

      if (
        !termsPopupAnnouncementList.value ||
        termsPopupAnnouncementList.value.length === 0
      )
        return;

      unagreedTermsPopupAnnouncementList.value = [
        ...termsPopupAnnouncementList.value
      ]
        // パートナーが同意済みの規約は通知対象から除外
        .filter(announcement => {
          return (
            announcement.service_term.id &&
            !isAgreedTerms(announcement.service_term.id)
          );
        })
        // 1つの規約バージョンに対し、最終更新日がより新しい通知1つだけを表示
        .sort((a, b) => (a.updated_at > b.updated_at ? -1 : 1))
        .reduce(
          (announcementList: ApiResponse.Announcement[], announcement) => {
            const termsIdList: number[] = announcementList.reduce(
              (arr: number[], val) =>
                !!val.service_term.id ? [...arr, val.service_term.id] : arr,
              []
            );
            return announcement.service_term.id &&
              termsIdList.includes(announcement.service_term.id)
              ? announcementList
              : [...announcementList, announcement];
          },
          []
        );

      if (allTermsPopupAgreed.value) return;

      showNextAnnouncement(0);
    },
    { immediate: true }
  );

  return {
    isTermsPage,
    isTermsPopupVisible,
    runGetTermsPopupAnnouncements,
    displayedAnnouncement,
    showNextAnnouncement,
    deleteAnnouncementFromUnagreedList,
    onCompleteTermsAgree,
    allTermsPopupAgreed
  };
};
